import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import * as transactionService from "@/shared/api/services/transactionService";
import * as engineService from "@/shared/api/services/engineService";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/shared/components/ui/sheet";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Calendar } from "@/shared/components/ui/calendar";
import {
  Search,
  Calendar as CalendarIcon,
  ArrowDownToLine,
  ArrowUpFromLine,
  Copy,
  Check,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  X,
  Download,
  Webhook,
} from "lucide-react";
import EngineLayout from "@/shared/components/engine/EngineLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

export default function EngineTransactions() {
  const { t, language } = useLanguage();
  
  const statusConfig = useMemo(() => ({
    success: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", label: t("success") },
    pending: { icon: Clock, color: "bg-amber-100 text-amber-700", label: t("pending") },
    processing: { icon: Loader2, color: "bg-blue-100 text-blue-700", label: t("processing") },
    failed: { icon: XCircle, color: "bg-red-100 text-red-700", label: t("failed") },
    expired: { icon: XCircle, color: "bg-slate-100 text-slate-700", label: t("expired") },
  }), [t, language]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [merchantFilter, setMerchantFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [selectedTx, setSelectedTx] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const resellerName = "Gaming Partners Ltd";

  const { data: transactions = [], isLoading } = useQuery({
    queryKey: ["engine-all-transactions"],
    queryFn: () => transactionService.getEngineTransactions(),
  });

  const { data: merchants = [] } = useQuery({
    queryKey: ["engine-merchants"],
    queryFn: () => engineService.getMyMerchants(),
  });

  const { data: webhookLogs = [] } = useQuery({
    queryKey: ["webhook-logs", selectedTx?.transaction_id],
    queryFn: async () => [], // TODO: Implement WebhookLog.filter API call
    enabled: !!selectedTx?.transaction_id,
  });

  // Apply filters
  const filteredTransactions = transactions.filter(tx => {
    if (search && !tx.transaction_id?.toLowerCase().includes(search.toLowerCase()) &&
        !tx.merchant_ref?.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter !== "all" && tx.status !== statusFilter) return false;
    if (typeFilter !== "all" && tx.type !== typeFilter) return false;
    if (merchantFilter !== "all" && tx.merchant_id !== merchantFilter) return false;
    if (dateRange.from && new Date(tx.created_date) < dateRange.from) return false;
    if (dateRange.to && new Date(tx.created_date) > dateRange.to) return false;
    return true;
  });

  const copyToClipboard = (e, id) => {
    e.stopPropagation();
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setTypeFilter("all");
    setMerchantFilter("all");
    setDateRange({ from: null, to: null });
  };

  const hasFilters = search || statusFilter !== "all" || typeFilter !== "all" || merchantFilter !== "all" || dateRange.from;

  return (
    <EngineLayout currentPage="EngineTransactions" resellerName={resellerName}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{t("transactions")}</h1>
            <p className="text-slate-500">{filteredTransactions.length} {t("transactions")}</p>
          </div>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            {t("exportCsv")}
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 space-y-4">
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder={t("searchHeaderPlaceholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select value={merchantFilter} onValueChange={setMerchantFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder={t("merchant")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allMerchants")}</SelectItem>
                {merchants.map(m => (
                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder={t("status")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allStatus")}</SelectItem>
                <SelectItem value="success">{t("success")}</SelectItem>
                <SelectItem value="pending">{t("pending")}</SelectItem>
                <SelectItem value="processing">{t("processing")}</SelectItem>
                <SelectItem value="failed">{t("failed")}</SelectItem>
              </SelectContent>
            </Select>

            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder={t("type")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allTypes")}</SelectItem>
                <SelectItem value="deposit">{t("deposits")}</SelectItem>
                <SelectItem value="withdrawal">{t("withdrawals")}</SelectItem>
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  {dateRange.from ? (
                    dateRange.to ? (
                      `${format(dateRange.from, "MMM d")} - ${format(dateRange.to, "MMM d")}`
                    ) : format(dateRange.from, "MMM d, yyyy")
                  ) : t("dateRange")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={(range) => setDateRange(range || { from: null, to: null })}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>

            {hasFilters && (
              <Button variant="ghost" onClick={clearFilters} className="text-slate-500">
                <X className="h-4 w-4 mr-1" />
                {t("clearFilters")}
              </Button>
            )}
          </div>
        </div>

        {/* Transactions Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("transaction")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("merchant")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("type")}</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("amount")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("status")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("date")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-12 text-slate-500">{t("loading")}</td>
                  </tr>
                ) : filteredTransactions.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-12 text-slate-500">{t("noTransactionsFound")}</td>
                  </tr>
                ) : (
                  filteredTransactions.map((tx, index) => {
                    const statusKey = (tx.status || '').toLowerCase();
                    const status = statusConfig[statusKey] || statusConfig.pending;
                    const StatusIcon = status.icon;

                    return (
                      <motion.tr
                        key={tx.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.02 * index }}
                        onClick={() => setSelectedTx(tx)}
                        className="hover:bg-slate-50 transition-colors cursor-pointer"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                              tx.type === "deposit" ? "bg-sky-100" : "bg-emerald-100"
                            }`}>
                              {tx.type === "deposit" ? (
                                <ArrowDownToLine className="h-4 w-4 text-sky-600" />
                              ) : (
                                <ArrowUpFromLine className="h-4 w-4 text-emerald-600" />
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-1.5">
                                <span className="font-mono text-sm font-medium">
                                  {tx.transaction_id?.slice(0, 12)}...
                                </span>
                                <button
                                  onClick={(e) => copyToClipboard(e, tx.transaction_id)}
                                  className="text-slate-400 hover:text-slate-600"
                                >
                                  {copiedId === tx.transaction_id ? (
                                    <Check className="h-3.5 w-3.5 text-emerald-500" />
                                  ) : (
                                    <Copy className="h-3.5 w-3.5" />
                                  )}
                                </button>
                              </div>
                              <p className="text-xs text-slate-400">{tx.provider_code}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-slate-900">{tx.merchant_code || t("nA")}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-sm font-medium capitalize ${
                            tx.type === "deposit" ? "text-sky-600" : "text-emerald-600"
                          }`}>
                            {tx.type === "deposit" ? t("deposit") : t("withdrawal")}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="font-semibold">฿{tx.amount?.toLocaleString()}</span>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={`${status.color} gap-1 border-0`}>
                            <StatusIcon className={`h-3 w-3 ${tx.status === "processing" ? "animate-spin" : ""}`} />
                            {status.label}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">
                          {tx.created_date ? format(new Date(tx.created_date), "MMM d, HH:mm") : t("nA")}
                        </td>
                      </motion.tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Transaction Detail Sheet */}
        <Sheet open={!!selectedTx} onOpenChange={() => setSelectedTx(null)}>
          <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
            {selectedTx && (
              <>
                <SheetHeader className="pb-4 border-b">
                  <SheetTitle className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      selectedTx.type === "deposit" ? "bg-sky-100" : "bg-emerald-100"
                    }`}>
                      {selectedTx.type === "deposit" ? (
                        <ArrowDownToLine className="h-5 w-5 text-sky-600" />
                      ) : (
                        <ArrowUpFromLine className="h-5 w-5 text-emerald-600" />
                      )}
                    </div>
                    <div>
                      <p className="text-lg capitalize">{selectedTx.type === "deposit" ? t("deposit") : t("withdrawal")}</p>
                      <Badge className={`${statusConfig[(selectedTx.status || '').toLowerCase()]?.color} border-0`}>
                        {statusConfig[(selectedTx.status || '').toLowerCase()]?.label}
                      </Badge>
                    </div>
                  </SheetTitle>
                </SheetHeader>

                <div className="py-6 space-y-6">
                  {/* Amount */}
                  <div className="text-center py-4 bg-slate-50 rounded-xl">
                    <p className="text-sm text-slate-500 mb-1">{t("amount")}</p>
                    <p className={`text-3xl font-bold ${
                      selectedTx.type === "deposit" ? "text-sky-600" : "text-emerald-600"
                    }`}>
                      ฿{selectedTx.amount?.toLocaleString()}
                    </p>
                    {selectedTx.fee_amount && (
                      <p className="text-sm text-slate-400 mt-1">
                        {t("fee")}: ฿{selectedTx.fee_amount?.toLocaleString()}
                      </p>
                    )}
                  </div>

                  {/* Details */}
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-slate-500">{t("merchant")}</p>
                        <p className="font-medium">{selectedTx.merchant_code}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">{t("provider")}</p>
                        <p className="font-medium">{selectedTx.provider_code}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">{t("method")}</p>
                        <p className="font-medium capitalize">{selectedTx.payment_method?.replace(/_/g, " ")}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">{t("ref")}</p>
                        <p className="font-medium">{selectedTx.merchant_ref || t("nA")}</p>
                      </div>
                    </div>

                    {/* Transaction ID */}
                    <div>
                      <p className="text-sm text-slate-500 mb-2">{t("transactionId")}</p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 bg-slate-100 px-3 py-2 rounded-lg font-mono text-sm">
                          {selectedTx.transaction_id}
                        </code>
                        <Button variant="outline" size="icon" onClick={(e) => copyToClipboard(e, selectedTx.transaction_id)}>
                          {copiedId === selectedTx.transaction_id ? (
                            <Check className="h-4 w-4 text-emerald-500" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Status Timeline */}
                    <div>
                      <p className="text-sm text-slate-500 mb-3">{t("statusTimeline")}</p>
                      <div className="space-y-3">
                        {[
                          { label: t("createdLabel"), time: selectedTx.created_date, done: true },
                          { label: t("processingLabel"), time: selectedTx.processed_at, done: ["processing", "success", "failed"].includes(selectedTx.status) },
                          { label: selectedTx.status === "failed" ? t("failedLabel") : t("completedLabel"), time: selectedTx.completed_at, done: ["success", "failed"].includes(selectedTx.status), failed: selectedTx.status === "failed" },
                        ].map((step, i) => (
                          <div key={i} className="flex gap-3">
                            <div className="flex flex-col items-center">
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                step.done ? (step.failed ? "bg-red-100" : "bg-emerald-100") : "bg-slate-100"
                              }`}>
                                {step.done ? (
                                  step.failed ? (
                                    <XCircle className="h-3 w-3 text-red-600" />
                                  ) : (
                                    <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                                  )
                                ) : (
                                  <Clock className="h-3 w-3 text-slate-400" />
                                )}
                              </div>
                              {i < 2 && <div className={`w-0.5 h-6 ${step.done ? "bg-emerald-200" : "bg-slate-200"}`} />}
                            </div>
                            <div>
                              <p className={`text-sm font-medium ${step.done ? "text-slate-900" : "text-slate-400"}`}>
                                {step.label}
                              </p>
                              {step.time && step.done && (
                                <p className="text-xs text-slate-500">{format(new Date(step.time), "MMM d, HH:mm:ss")}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Callback Status */}
                    <div>
                      <p className="text-sm text-slate-500 mb-2">{t("callbackStatus")}</p>
                      <Badge className={
                        (() => {
                          const statusLower = (selectedTx.callback_status || "pending").toLowerCase();
                          return statusLower === "success" || statusLower === "delivered" 
                            ? "bg-emerald-100 text-emerald-700"
                            : statusLower === "failed" 
                            ? "bg-red-100 text-red-700"
                            : "bg-amber-100 text-amber-700";
                        })()
                      }>
                        {(() => {
                          const status = (selectedTx.callback_status || "pending").toLowerCase();
                          // Map backend status values to translation keys
                          if (status === "success" || status === "delivered") return t("delivered");
                          if (status === "failed") return t("failed");
                          return t("pending");
                        })()}
                      </Badge>
                      {selectedTx.callback_attempts > 0 && (
                        <span className="text-sm text-slate-500 ml-2">
                          ({selectedTx.callback_attempts} {t("attempts")})
                        </span>
                      )}
                    </div>

                    {/* Webhook Logs */}
                    {webhookLogs.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Webhook className="h-4 w-4 text-slate-400" />
                          <p className="text-sm text-slate-500">{t("webhookLogs")}</p>
                        </div>
                        <pre className="bg-slate-900 text-slate-100 p-4 rounded-xl text-xs overflow-x-auto max-h-40">
                          {JSON.stringify(webhookLogs[0], null, 2)}
                        </pre>
                      </div>
                    )}

                    {/* Error Message */}
                    {selectedTx.error_message && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                        <p className="text-sm font-medium text-red-800">{t("error")}</p>
                        <p className="text-sm text-red-700 mt-1">{selectedTx.error_message}</p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </EngineLayout>
  );
}